import { Injectable, OnDestroy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, ChildActivationEnd, NavigationEnd, NavigationStart, ParamMap, Router, Scroll } from '@angular/router';

import { BehaviorSubject, NEVER, Observable, Subject, Subscription, merge, of } from 'rxjs';
import { mergeMap, pairwise, startWith } from 'rxjs/operators';

import { DiscoverRouteModel } from '../models/discover-route.model';
import { DiscoverRouteTypeEnum } from '../enums/discover-route-type.enum';
import { EntityType } from '../../../api/targeting/enum/entity.type.enum';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';

@Injectable()
export class DiscoverRoutingService implements OnDestroy {
    /** Breaks the URL into known pieces so the current location can be evaluated */
    // eslint-disable-next-line max-len
    private urlParse: RegExp = /Discover\/?(targetingsearches|targetinglists|privatecontacts|distributions|profile|influencerstreams|portfolios)?\/?(dashboard|csvimport|distribution|search|list|editor|contact|outlet|new|results)?\/?([a-f0-9\-]+)?\/?(results|update|report)?/;

    private discoverRootPath: string = 'Discover';
    private queryParamMap: ParamMap;
    private currentRoute: DiscoverRouteModel;
    private currentRouteProvider: BehaviorSubject<DiscoverRouteModel>;
    private cachedNavigationStart: NavigationStart;
    private navOutsideDiscover$ = new Subject<null>();
    private isReload: boolean = true;

    private subscriptions: Subscription[] = [];

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private titleService: Title,
        private localizationService: LocalizationService
    ) {
        this.currentRoute = new DiscoverRouteModel(null);
        this.currentRouteProvider = new BehaviorSubject<DiscoverRouteModel>(this.currentRoute);

        this.subscriptions.push(
            this.router.events.subscribe((e) => {
                if (e instanceof NavigationStart) {
                    this.cachedNavigationStart = e;
                }

                if (e instanceof Scroll && this.isReload) {
                    this.isReload = false;
                    if (e.routerEvent instanceof NavigationEnd) {
                        this.updateCurrentRoute(e.routerEvent.urlAfterRedirects);
                        this.setBrowserTitle();
                    }
                }

                if (e instanceof NavigationEnd) {
                    this.isReload = false;
                    this.updateCurrentRoute(e.urlAfterRedirects);
                    this.setBrowserTitle();
                }
                // child activation end occurs before navigation end
                if (e instanceof ChildActivationEnd) {
                    this.queryParamMap = e.snapshot.queryParamMap;
                }
                if (!this.queryParamMap) {
                    this.queryParamMap = this.route.snapshot.queryParamMap;
                }

            }),
        );

        this.initialRouteCheck(this.router.url);
    }

    /**
     * Checks if the initial route will properly load data, and if not, then go to the default route
     * Default route is first topic if it exists, and otherwise search.
     * @param url The current full URL
     */
    private initialRouteCheck(url: string) {
        const matches = url.match(this.urlParse) || [];

        if (!matches) {
            // Not a targeting route
            return;
        } else {
            switch (matches[1]) {
                case 'targetingsearches':
                case 'targetinglists':
                case 'privatecontacts':
                case 'distributions':
                case 'profile':
                    this.updateCurrentRoute(url);
                    break;

                default:
                    // this.gotoDefault();
                    break;
            }
        }
    }

    /**
     * Updates the current route from a URL
     * @param url The current full URL
     */
    private updateCurrentRoute(url: string): void {
        const route = this.getRouteModel(url);

        if (!route) {
            this.navOutsideDiscover$.next(null);
            return;
        }

        this.currentRoute = route;
        this.currentRoute.queryParamMap = this.queryParamMap;
        this.currentRouteProvider.next(this.currentRoute);
    }

    private setBrowserTitle() {
        let child = this.route.firstChild;
        while (child.firstChild) {
            child = child.firstChild;
        }
        if (!!child.snapshot.data['title']) {
            this.titleService.setTitle(
                this.localizationService.transform(
                    child.snapshot.data['title']
                )
            );
        }
    }


    /**
     * Returns a route model based on a url passed in
     *
     * @param url -  url to base route model on
     *
     * @returns MonitoringRouteModel
     */
    getRouteModel(url: string): DiscoverRouteModel {
        const matches = url.match(this.urlParse);
        let route: DiscoverRouteModel;

        if (!matches) {
            return;
        } else {
            switch (matches[1]) {
                case 'targetingsearches':
                    if (matches[2] === 'search') {
                        const id = matches[3];
                        if (matches[4] === 'results') {
                            if (id && !isNaN(+id)) {
                                route = new DiscoverRouteModel(DiscoverRouteTypeEnum.targetingSavedSearchResults, id);
                            } else {
                                route = new DiscoverRouteModel(DiscoverRouteTypeEnum.targetingResults);
                            }
                        } else {
                            if (id && !isNaN(+id)) {
                                route = new DiscoverRouteModel(DiscoverRouteTypeEnum.targetingSavedSearch, id);
                            } else {
                                if (matches[4] === 'update') {
                                    route = new DiscoverRouteModel(DiscoverRouteTypeEnum.targetingSearchUpdate);
                                } else {
                                    route = new DiscoverRouteModel(DiscoverRouteTypeEnum.targetingSearch);
                                }
                            }
                        }
                    } else {
                        route = new DiscoverRouteModel(DiscoverRouteTypeEnum.targetingSearchHype);
                    }
                    break;
                case 'targetinglists':
                    if (matches[2] === 'list') {
                        const id = matches[3];
                        if (matches[4] === 'results') {
                            if (id && !isNaN(+id)) {
                                route = new DiscoverRouteModel(DiscoverRouteTypeEnum.targetingListSearchResult, id);
                            }
                        }
                    } else {
                        route = new DiscoverRouteModel(DiscoverRouteTypeEnum.targetingListHype);
                    }
                    break;
                case 'privatecontacts':
                    if (matches[2] === 'editor') {
                        const id = matches[3];
                        if (id) {
                            route = new DiscoverRouteModel(DiscoverRouteTypeEnum.contactEditor, id);
                        } else {
                            route = new DiscoverRouteModel(DiscoverRouteTypeEnum.contactEditor);
                        }
                    } else if (matches[2] === 'csvimport') {
                        route = new DiscoverRouteModel(DiscoverRouteTypeEnum.csvImport);
                    } else {
                        route = new DiscoverRouteModel(DiscoverRouteTypeEnum.privateContacts);
                    }
                    break;
                case 'distributions':
                    if (matches[2] === 'distribution') {
                        const id = matches[3];
                        if (!id) {
                            route = new DiscoverRouteModel(DiscoverRouteTypeEnum.distributionNew);
                            break;
                        }
                        if (matches[4] === 'report') {
                            route = new DiscoverRouteModel(DiscoverRouteTypeEnum.distributionReport, id);
                        } else {
                            route = new DiscoverRouteModel(DiscoverRouteTypeEnum.distributionEdit, id);
                            if (this.queryParamMap?.get('step')) {
                                route = new DiscoverRouteModel(DiscoverRouteTypeEnum.distributionEdit, id, this.queryParamMap);
                            }
                        }
                    } else if (matches[2] === 'dashboard') {
                        route = new DiscoverRouteModel(DiscoverRouteTypeEnum.distributionDashboard);
                    } else if (matches[2] === 'new') {
                        route = new DiscoverRouteModel(DiscoverRouteTypeEnum.newDistribution);
                    } else {
                        route = new DiscoverRouteModel(DiscoverRouteTypeEnum.distributionHype);
                    }
                    break;

                case 'profile':
                    if (!this.queryParamMap) {
                        break;
                    }

                    const queryParamMap = this.queryParamMap;

                    if (matches[2] === 'contact') {
                        let contactId = matches[3];
                        if (!contactId) {
                            contactId = undefined;
                        }
                        queryParamMap['entityType'] = EntityType.CONTACT;
                        route = new DiscoverRouteModel(DiscoverRouteTypeEnum.contactProfile, contactId, queryParamMap);

                        break;
                    }

                    if (matches[2] === 'outlet') {
                        let outletId = matches[3];
                        if (!outletId) {
                            outletId = undefined;
                        }

                        queryParamMap['entityType'] = EntityType.OUTLET;
                        route = new DiscoverRouteModel(DiscoverRouteTypeEnum.outletProfile, outletId, queryParamMap);
                        break;
                    }

                    route = new DiscoverRouteModel(DiscoverRouteTypeEnum.targetingSearchHype);
                    break;
                case 'influencerstreams' :
                    route = new DiscoverRouteModel(DiscoverRouteTypeEnum.influencerStreams);
                    break;
                case 'portfolios' : {
                    if (matches[2] === 'results') {
                        route = new DiscoverRouteModel(DiscoverRouteTypeEnum.portfolioResults, matches[3]);
                    }
                    break;
                }
                default:
                    // Inside amplify, but unrecognized, so go to default
                    route = new DiscoverRouteModel(DiscoverRouteTypeEnum.targetingSearchHype);
                    break;
            }
        }
        return route;
    }

    /**
     * Given an observable that tracks the current mode, returns an observable that tracks the
     * current mode and whether it has changed. Useful because subscriptions to this service's currentRoute
     * fire even when we are outside the feature. This allows us to determine whether we are leaving or entering
     * the feature (and subfeature/mode).
     * @param getCurrentMode$ An observable that emits the current mode on route event
     * @returns An observable that emits on route event, detailing whether the mode has changed from previous value
     */
    public getModeChange(
        getCurrentMode$: Observable<number>
    ): Observable<{ isModeChange: boolean; currentMode: number}> {
        return merge(
            this.navOutsideDiscover$,
            getCurrentMode$
        ).pipe(
            startWith(null),
            pairwise(),
            mergeMap(([prev, curr]) => {
                if (curr === null) {
                    // We are currently outside discover module
                    return NEVER;
                } else {
                    // False means navigation with a subfeature
                    // True means navigation to a new subfeature
                    return of({
                        isModeChange: prev !== curr,
                        currentMode: curr
                    });
                }
            }),
        );
    }

    /**
     * Gets an observable for the current route
     *
     * @returns An observable that's updated each time the route changes
     */
    public getCurrentRoute(): Observable<DiscoverRouteModel> {
        return this.currentRouteProvider;
    }

    /**
     * Returns the last cached NavigationStart object
     *
     * @returns NavigationStart
     */
    public getNextRoute(): DiscoverRouteModel {
        return this.getRouteModel(this.cachedNavigationStart.url);
    }


    gotoTargetingHype(): void {
        this.goto(['targetingsearches']);
    }

    gotoListHype(): void {
        this.goto(['targetinglists']);
    }

    gotoDistributionHype(): void {
        this.goto(['distributions']);
    }

    gotoDistribution(id?: number): void {
        if (id) {
            this.goto(['distributions', 'distribution', id]);
        } else {
            this.goto(['distributions', 'distribution']);
        }
    }

    gotoDistributionReport(id?: number): void {
        this.goto(['distributions', 'distribution', id, 'report']);
    }

    gotoDistributionDashboard(): void {
        this.goto(['distributions', 'dashboard']);
    }

    gotoTargetingSearch(id?: number, update: boolean = false): void {
        const path: (string|number)[] = ['targetingsearches', 'search'];
        if (id) {
            path.push(id);
        }
        if (update) {
            path.push('update');
        }
        this.goto(path);
    }

    gotoTargetingSearchWithParams(params: any): void {
        const path: (string|number)[] = ['targetingsearches', 'search'];
        this.goto(path, false, params);
    }

    gotoTargetingSearchResults(id?: number): void {
        if (id) {
            this.goto(['targetingsearches', 'search', id, 'results']);
        } else {
            this.goto(['targetingsearches', 'search', 'results']);
        }
    }

    gotoListSearchResults(id: number): void {
        this.goto(['targetinglists', 'list', id, 'results']);
    }

    gotoPrivateContacts(): void {
        this.goto(['privatecontacts']);
    }

    gotoTargetingSavedSearch(id: number): void {
        this.goto(['targetingsearches', 'search', id]);
    }

    gotoTargetingSavedSearchResults(id: number): void {
        this.goto(['targetingsearches', 'search', id, 'results']);
    }

    gotoContactEditor(id?: string): void {
        if (id) {
            this.goto(['privatecontacts', 'editor', id]);
        } else {
            this.goto(['privatecontacts', 'editor']);
        }
    }
    gotoCsvImport(): void {
        this.goto(['privatecontacts', 'csvimport']);
    }

    goToContactProfile(id: string) {
        this.goto(['profile', 'contact', id]);
    }

    goToOutletProfile(id: string) {
        this.goto(['profile', 'outlet', id]);
    }

    goToNewDistribution() {
        this.goto(['distributions', 'new']);
    }

    goToDistributionWithParams(id: number, params: any) {
        this.goto(['distributions', 'distribution', id], true, params);
    }


    gotoViNew() {
        this.goto(['visibility-intelligence', 'new']);
    }

    /**
     * Routes to a monitoring section
    */
    private goto(path: (string | number)[], replaceURL: boolean = false, queryParams: any = {}) {
        this.router.navigate(
            [this.discoverRootPath, ...path],
            // Optionally replace the previous URL in the browser history
            {
                replaceUrl: replaceURL,
                queryParams: queryParams
            }
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}

